import { useComparer } from '@/hooks/useComparer';
import { useExpandable } from '@/hooks/useExpandable';
import { useProgressiveList } from '@/hooks/useProgressiveList';
import { ComparerNode, ComparerSubroutine } from '@/types/comparer';
import { isArray } from '@/util/typeGuards';
import { useMemo, type FC } from 'react';
import ModifiedProps from '../comparerShared/ModifiedProps';
import StatusRow from '../comparerShared/StatusRow';
import { getVisibleItems, getVisibleParams, sortComparerIds } from '../comparerUtils/comparerUtils';

type NodesChangesProps = {
  subroutines: ComparerSubroutine | ComparerSubroutine[];
};

type NodeChangesProps = {
  node: ComparerNode;
};

const NodeChanges: FC<NodeChangesProps> = ({ node }) => {
  const { showUnchanged } = useComparer();
  const [open, setOpen] = useExpandable(node.status === 'modified');

  const visibleBlocks = useMemo(
    () =>
      node.changedBlocks
        ? [
            getVisibleParams(node.changedBlocks.node, showUnchanged),
            getVisibleParams(node.changedBlocks.details, showUnchanged),
            getVisibleParams(node.changedBlocks.portoptions, showUnchanged),
            getVisibleParams(node.changedBlocks.connections, showUnchanged),
            getVisibleParams(node.changedBlocks.event_handlers, showUnchanged)
          ].flat()
        : [],
    [node.changedBlocks, showUnchanged]
  );
  const expandable = visibleBlocks.length > 0;

  return (
    <>
      <StatusRow
        status={node.status}
        title={node.id}
        label={node.label}
        open={open}
        setOpen={expandable ? setOpen : undefined}
      />
      {open &&
        expandable &&
        visibleBlocks.map((change, index) => <ModifiedProps key={`${change.key}-${index}`} param={change} />)}
    </>
  );
};

const NodesChanges: FC<NodesChangesProps> = ({ subroutines }) => {
  const { showUnchanged } = useComparer();

  const sortedNodes = useMemo(() => {
    const extractedNodes: ComparerNode[] = [];
    const subroutineNodes = isArray(subroutines)
      ? subroutines.map((subroutine) => subroutine.changes?.nodeChanges)
      : [subroutines.changes?.nodeChanges];

    subroutineNodes.forEach((nodes) => {
      nodes?.forEach((node) => {
        extractedNodes.push(node);
      });
    });

    return sortComparerIds(getVisibleItems(extractedNodes, showUnchanged));
  }, [subroutines, showUnchanged]);

  const progressiveNodes = useProgressiveList(sortedNodes);

  return progressiveNodes.map((node, index) => <NodeChanges key={`${node.id}-${index}`} node={node} />);
};

export default NodesChanges;
